import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import '../styles/CookieConsent.css';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_SETTINGS_KEY = 'cookie_settings';

const CookieConsent = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    essential: true, // Always true
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedSettings = localStorage.getItem(COOKIE_SETTINGS_KEY);
    
    if (!savedConsent) {
      setShowBanner(true);
    }
    
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleAcceptAll = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setSettings(newSettings);
    saveConsent(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const newSettings = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setSettings(newSettings);
    saveConsent(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    saveConsent(settings);
    setShowSettings(false);
    setShowBanner(false);
  };

  const saveConsent = (newSettings) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(newSettings));
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: newSettings }));
  };

  const toggleSetting = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be toggled
    setSettings(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Backdrop */}
      {(showBanner || showSettings) && (
        <div 
          className="cookie-consent-backdrop"
        />
      )}

      {/* Main Banner */}
      {showBanner && (
        <div className="cookie-consent-banner">
          <div className="cookie-consent-content">
            <h2>{t.cookieConsent.title}</h2>
            <p>{t.cookieConsent.description}</p>
            <div className="cookie-consent-buttons">
              <button 
                onClick={handleAcceptAll}
                className="cookie-consent-button primary"
              >
                {t.cookieConsent.acceptAll}
              </button>
              <button 
                onClick={handleRejectAll}
                className="cookie-consent-button secondary"
              >
                {t.cookieConsent.rejectAll}
              </button>
              <button 
                onClick={() => setShowSettings(true)}
                className="cookie-consent-button secondary"
              >
                {t.cookieConsent.customize}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="cookie-consent-settings">
          <div className="cookie-consent-settings-content">
            <h2>{t.cookieConsent.settingsTitle}</h2>
            
            <div className="cookie-consent-options">
              <div className="cookie-consent-option">
                <div className="cookie-consent-option-header">
                  <h3>{t.cookieConsent.essential.title}</h3>
                  <div className="cookie-consent-toggle disabled">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      readOnly
                    />
                    <span className="cookie-consent-slider"></span>
                  </div>
                </div>
                <p>{t.cookieConsent.essential.description}</p>
              </div>

              <div className="cookie-consent-option">
                <div className="cookie-consent-option-header">
                  <h3>{t.cookieConsent.analytics.title}</h3>
                  <div className="cookie-consent-toggle">
                    <input
                      type="checkbox"
                      checked={settings.analytics}
                      onChange={() => toggleSetting('analytics')}
                    />
                    <span className="cookie-consent-slider"></span>
                  </div>
                </div>
                <p>{t.cookieConsent.analytics.description}</p>
              </div>

              <div className="cookie-consent-option">
                <div className="cookie-consent-option-header">
                  <h3>{t.cookieConsent.marketing.title}</h3>
                  <div className="cookie-consent-toggle">
                    <input
                      type="checkbox"
                      checked={settings.marketing}
                      onChange={() => toggleSetting('marketing')}
                    />
                    <span className="cookie-consent-slider"></span>
                  </div>
                </div>
                <p>{t.cookieConsent.marketing.description}</p>
              </div>
            </div>

            <div className="cookie-consent-settings-buttons">
              <button 
                onClick={handleSaveSettings}
                className="cookie-consent-button primary"
              >
                {t.cookieConsent.saveSettings}
              </button>
              <button 
                onClick={() => setShowSettings(false)}
                className="cookie-consent-button secondary"
              >
                {t.cookieConsent.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent; 